import { useEffect, useState } from "react";
import { signupFormLogic } from "./formLogic";
import Styles from "./index.module.scss";
import Partition from "../../shared/elements/partition";
import SocialLoginButtons from "../../shared/elements/socialLoginButton";
import AuthForm from "../../shared/sections/auth-form";
import { useAuthFrontLock } from '../../../context/useAuthFrontContext';
import { useScreen } from '../../../context/useScreen';
import { useLoader } from '../../../context/useLoader';
import validateEmail from '../../../utils/validations/emailValidation';
import ErrorBar from "../../shared/elements/errorBar";
import i18n from "../../../utils/translations/i18n";
import { COOKIE_CONSTANT, ERROR_CODE, FALLBACK_LOCALE, PAGE_NAME } from "../../../utils/constant/constant";
import { deleteCookie, redirectToAuthcallback, setCurrentPageCookie } from "../../../utils/utils/utils";

const SignUp = () => {
    const { t } = i18n;
    const [form, setFormState] = useState(JSON.parse(JSON.stringify(signupFormLogic)));
    const [errorBarState, setErrorBarState] = useState({ isActive: false, errorType: '', message: '' });
    const authFrontLock = useAuthFrontLock();
    const screen = useScreen();
      const { showLoader, hideLoader } = useLoader();

    const errorMessages = {
        [ERROR_CODE.NULL_ERROR]: t('sign_up.mandatory_field_error'),
        [ERROR_CODE.GENERIC_ERROR]: t('sign_up.generic_error'),
        [ERROR_CODE.SOCIAL_SIGNIN_ERROR]: t('sign_in.social_signin_error'),
        [ERROR_CODE.EMAIL_NOT_PRESENT]: t('sign_in.social_signin_email_not_present_error')
    };

    useEffect(() => {
        document.title = t('sign_up.page_title');

        const additionalParams = authFrontLock?.getAdditionalParams();
        setCurrentPageCookie( PAGE_NAME.SIGN_UP, additionalParams?.screenId );
    }, []);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormState(prevForm => ({
            ...prevForm,
            [name]: {...prevForm[name], value, dangerWarn: false, errorMessage: null}
        }));
    };

    const resetFormFieldsErrors = () => {
        const formObj = { ...form };
        formObj.formFields.forEach(field => {
            formObj[field].dangerWarn = false;
            formObj[field].errorMessage = null;
        });
        setFormState(formObj);
    };

    const showErrorMessage = (error, email?) => {
        if(error === ERROR_CODE.REAUTHENTICATION_REQUIRED ){
            deleteCookie(COOKIE_CONSTANT.CURRENT_PAGE_COOKIE_KEY);
            authFrontLock && redirectToAuthcallback(authFrontLock, ERROR_CODE.REAUTHENTICATION_REQUIRED, email)
            return;
        }
        if (error === ERROR_CODE.SOCIAL_SIGNIN_ERROR){
            resetFormFieldsErrors();
        }
        if (error === ERROR_CODE.USER_EXISTS) {
            setFormState(prevForm => ({
                ...prevForm,
                email: {
                    ...prevForm.email,
                    dangerWarn: true,
                    errorMessage: t('sign_up.email_already_exist_error'),
                }
            }));
        } else if (errorMessages[error]) {
            setErrorBarState({ isActive: true, errorType: error, message: errorMessages[error] });
        } else {
            setErrorBarState({ isActive: true, errorType: ERROR_CODE.GENERIC_ERROR, message: errorMessages[ERROR_CODE.GENERIC_ERROR] });
        }
    };

    const handleBlur = async (event) => {
        const fieldName = event.target.name;
        const { regex, value } = form[fieldName];
        const expression = new RegExp(regex);

        const showRegexError = value && !expression.test(value);
        const showNullError = !value && errorBarState.isActive && errorBarState.errorType === ERROR_CODE.NULL_ERROR;
        let showDangerWarn = false;

        let errorMessage:any = null;
        if (showRegexError) {
            if(fieldName === 'email') {
                errorMessage = t('sign_up.invalid_email_error');
            } else if (value.length < 8) {
                errorMessage = t('sign_up.password_length_error');
            } else {
                errorMessage = t('sign_up.password_policy_error');
            }
        } else if (value && fieldName === 'password' && form.email.value.toLowerCase() === value.toLowerCase()) {
            errorMessage = t('sign_up.common_password_error');
            showDangerWarn = true;
        }

        setFormState(prevForm => ({
            ...prevForm,
            [fieldName]: {
                ...prevForm[fieldName],
                dangerWarn: showRegexError || showNullError || showDangerWarn,
                errorMessage,
            }
        }));
    };

    const validateForm = () => {
        const formObj = { ...form };
        let regexError = false, nullError = false;

        formObj.formFields.forEach(field => {
            const { regex, value, required } = formObj[field];
            const expression = new RegExp(regex);
            formObj[field].dangerWarn = false;
            formObj[field].errorMessage = '';

            if (value !== '' && !expression.test(value)) {
                regexError = true;
                formObj[field].dangerWarn = true;

                if(field === 'email') {
                    formObj[field].errorMessage = t('sign_up.invalid_email_error');
                } else if (value.length < 8) {
                    formObj[field].errorMessage = t('sign_up.password_length_error');
                } else {
                    formObj[field].errorMessage = t('sign_up.password_policy_error');
                }
            }

            if (value === '' && required) {
                nullError = true;
                formObj[field].dangerWarn = true;
            }
        });

        if (!(regexError || nullError) && formObj.email.value.toLowerCase() === formObj.password.value.toLowerCase()) {
            formObj.password.dangerWarn = true;
            formObj.password.errorMessage = t('sign_up.common_password_error');
            regexError = true;
        }

        nullError ? showErrorMessage(ERROR_CODE.NULL_ERROR) : setErrorBarState({ isActive: false, errorType: '', message: '' });

        setFormState(formObj);
        return !(regexError || nullError);
    };

    const SignUp = async (event) => {
        try {
            event.preventDefault();
            showLoader();
            const email = form.email.value;
            const password = form.password.value;
            const ui_locales = authFrontLock.getUILocales();
            const additionalParams = {
                ui_locales: ui_locales && ui_locales.length > 0 ? ui_locales[0] : FALLBACK_LOCALE,
            }
            const signupRequestBody = { 
                email, 
                password, 
                captcha : authFrontLock.getCaptchaToken(),
                additionalParams
            }
            await authFrontLock.signupAndLoginAsync(signupRequestBody);
          } catch (err:any) {
            showErrorMessage(err.errorCode, form.email.value);
            hideLoader();
          }
    };

    const handleSubmit = async (event) => {
        try {
            event.preventDefault();
            if (validateForm()) {
                showLoader();
                const result = await validateEmail(form.email.value);
                hideLoader();
                if (result.isValid) {
                    // Proceed with signup
                    await SignUp(event);
                } else {
                    setFormState((prevForm) => ({
                        ...prevForm,
                        email: {
                            ...prevForm.email,
                            dangerWarn: true,
                            errorMessage: t('sign_up.invalid_email_error'),
                        },
                    }));
                }
            }
        } catch (err: any) { 
            showErrorMessage(ERROR_CODE.GENERIC_ERROR);
            hideLoader();
        }
    };

    const toggleShowPassword = (fieldName) => {
        const showPassword = !form[fieldName].showPassword;
        setFormState(prevForm => ({
            ...prevForm,
            [fieldName]: {
                ...prevForm[fieldName],
                showPassword,
                type: showPassword ? 'text' : 'password'
            }
        }));
    };

    const signIn = (event) => {
        event.preventDefault();
        screen.setDisplayScreen(PAGE_NAME.SIGN_IN);
    }

    return (
        <>
            <div className={`${Styles.container} option-a`}>
                <div className={Styles.header}>
                    <h2 className={Styles.title}>{t('sign_up.create_an_account')}</h2>
                    <div>
                        <span className={Styles.subtle}>{t('sign_in.or')}&nbsp;</span>
                        <span id="sign_in_title" onClick={signIn} className={Styles.boldlink} role="button" tabIndex={0}>{t('sign_in.title')}</span>
                    </div>
                </div>
                {errorBarState.isActive && <ErrorBar message={errorBarState.message} />}    
                <AuthForm isSignupForm={true} form={form} handleChange={handleChange} handleBlur={handleBlur} handleSubmit={handleSubmit} showPasswordHandler={toggleShowPassword} option='option-a'/>
                <div className={Styles.footer}>
                    <Partition breakPartition={false}/>
                    <div className={Styles.footerTitle}>
                        {t('sign_in.continue_with')}
                    </div>
                    <SocialLoginButtons showErrorMessage={showErrorMessage}/>
                </div>
            </div>
            <div className={`${Styles.container} option-b`}>
                <div className={`${Styles.header} ${Styles.additionalGap}`}>
                <h2 className={Styles.title}>{t('sign_up.create_an_account')}</h2>
                    {errorBarState.isActive && errorBarState.errorType===ERROR_CODE.SOCIAL_SIGNIN_ERROR && <ErrorBar message={errorBarState.message} />}
                    <SocialLoginButtons showErrorMessage={showErrorMessage}/>
                </div>
                <Partition breakPartition={true} text={t('sign_in.continue_with_email')}/>
                {errorBarState.isActive && errorBarState.errorType!==ERROR_CODE.SOCIAL_SIGNIN_ERROR && <ErrorBar message={errorBarState.message} />}    
                <AuthForm isSignupForm={true} form={form} handleChange={handleChange} handleBlur={handleBlur} handleSubmit={handleSubmit} showPasswordHandler={toggleShowPassword} option='option-b'/>
                <div className={`${Styles.footer} ${Styles.additionalGap}`}>
                    <Partition breakPartition={false}/>
                    <div>
                        <span className={Styles.subtle}>{t('sign_in.or')}&nbsp;</span>
                        <span onClick={signIn} className={Styles.boldlink}  role="button" tabIndex={0}>{t('sign_in.title')}</span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SignUp;